// Copyright (c) Ping. All rights reserved.
//
// Business Directory route host.
// Toggles between the directory listing view and the full-page create company flow.
//
// BE endpoints (all live — see openapi.yaml):
//   GET  /v1/directory/categories
//   GET  /v1/directory/companies
//   POST /v1/directory/companies
//   GET  /v1/directory/companies/mine
//   GET  /v1/directory/companies/{id}

import { useCallback, useEffect, useState } from 'react';

import {
  BusinessDirectoryView,
  type Company,
  CreateCompanyPage,
} from '@features/business-directory';

// Module-level flag: set to true by `requestCreateOnNextMount()` so that
// a navigation from the ConversationListPanel sidebar (where we can't
// pass component state across routes) opens the create flow immediately.
let pendingCreate = false;

/** Call this before routing to /business-directory to auto-open the create flow. */
export function requestCreateOnNextMount(): void {
  pendingCreate = true;
}

export function BusinessDirectoryRouteHost(): JSX.Element {
  const [creating, setCreating] = useState(false);

  // Consume the pending-create flag on first render so the flag is
  // cleared whether or not the component is later unmounted first.
  useEffect(() => {
    if (pendingCreate) {
      pendingCreate = false;
      setCreating(true);
    }
  }, []);

  const handleCreated = useCallback((_company: Company) => {
    // `CreateCompanyPage` has already written the company to Dexie
    // (`persistCompanyOptimistically`), so `useMyCompanies`' liveQuery shows
    // it immediately in the directory — even though the PUBLIC listing only
    // returns VERIFIED companies. Just return to the directory.
    setCreating(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (creating) {
    return (
      <CreateCompanyPage
        onCreated={handleCreated}
        onCancel={() => setCreating(false)}
      />
    );
  }

  return (
    <BusinessDirectoryView
      onCreateCompany={() => setCreating(true)}
    />
  );
}
